import { AuthManager, type AuthState } from '../auth/AuthManager';
import { AuthForm } from '../auth/AuthForm';
import { LandingPage } from '../ui/components/LandingPage';
import { LaunchingPage } from '../ui/components/LaunchingPage';
import { Salmon } from '../salmon';
import { UIManager } from '../ui/UIManager';

export type AppState = 'launching' | 'landing' | 'auth' | 'app';

export class RouteManager {
  private authManager: AuthManager;
  private currentState: AppState = 'launching';
  private appContainer: HTMLElement;
  private salmon: Salmon | null = null;
  private uiManager: UIManager | null = null;
  private unsubscribeAuth: (() => void) | null = null;

  constructor(appContainer: HTMLElement) {
    this.appContainer = appContainer;
    this.authManager = AuthManager.getInstance();
    this.setupAuthListener();
    this.setupURLRouting();
    this.initialize();
  }

  private setupAuthListener(): void {
    this.unsubscribeAuth = this.authManager.subscribe((authState: AuthState) => {
      this.handleAuthStateChange(authState);
    });
  }

  private setupURLRouting(): void {
    // Listen for URL changes
    window.addEventListener('popstate', () => {
      this.handleURLChange();
    });

    // Handle initial URL
    this.handleURLChange();
  }

  private handleURLChange(): void {
    const path = window.location.pathname;
    const authState = this.authManager.getAuthState();

    // If authenticated, always show app regardless of URL
    if (authState.isAuthenticated) {
      this.showApp();
      return;
    }

    // Route based on URL path
    switch (path) {
      case '/launching':
        this.showLaunching();
        break;
      case '/':
        this.showLanding();
        break;
      case '/login':
        this.showAuth();
        break;
      default:
        this.showInitialPage();
    }
  }

  private initialize(): void {
    const authState = this.authManager.getAuthState();
    this.handleAuthStateChange(authState);
  }

  private handleAuthStateChange(authState: AuthState): void {
    if (authState.isAuthenticated) {
      this.showApp();
    } else {
      // Add a small delay to ensure cleanup is complete
      setTimeout(() => {
        this.showInitialPage();
      }, 100);
    }
  }

  private showInitialPage(): void {
    // Check environment variable to determine initial page
    const showLaunchingPage = this.getEnvVariable('VITE_SHOW_LAUNCHING_PAGE', 'true');
    
    if (showLaunchingPage === 'true') {
      this.showLaunching();
    } else {
      this.showLanding();
    }
  }

  private getEnvVariable(key: string, defaultValue: string = ''): string {
    // In Vite, environment variables are available on import.meta.env
    return import.meta.env[key] || defaultValue;
  }

  private showLaunching(): void {
    this.clearContainer();
    this.currentState = 'launching';
    this.updateURL('/launching');
    
    const launchingPage = new LaunchingPage(() => {
      this.showLanding();
    });
    
    this.appContainer.appendChild(launchingPage.getElement());
  }

  private showLanding(): void {
    this.clearContainer();
    this.currentState = 'landing';
    this.updateURL('/');
    
    const landingPage = new LandingPage(() => {
      this.showAuth();
    });
    
    this.appContainer.appendChild(landingPage.getElement());
  }

  private showAuth(): void {
    this.clearContainer();
    this.currentState = 'auth';
    this.updateURL('/login');
    
    const authForm = new AuthForm(() => {
      // Auth success is handled by the auth state listener
    });
    
    this.appContainer.appendChild(authForm.getElement());
  }

  private showApp(): void {
    // Prevent duplicate initialization
    if (this.currentState === 'app' && this.salmon) {
      return;
    }
    
    this.clearContainer();
    this.currentState = 'app';
    this.updateURL('/app');
    
    // Initialize the Salmon app
    this.salmon = new Salmon(this.appContainer);
    this.salmon.salmon_ASCII();
    
    // Initialize UI Manager
    this.uiManager = new UIManager(this.salmon.canvas, this.salmon.canvas.app);
    this.uiManager.init();
  }

  private updateURL(path: string): void {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }

  private clearContainer(): void {
    try {
      // Clean up UI manager first to remove event listeners
      if (this.uiManager) {
        this.uiManager.cleanup();
        this.uiManager = null;
      }
      
      // Clean up existing app if it exists
      if (this.salmon) {
        this.salmon.canvas.destroy();
        this.salmon = null;
      }

      // Clear the container
      this.appContainer.innerHTML = '';
      
      // Remove any orphaned canvas elements
      const canvasElements = document.querySelectorAll('canvas');
      canvasElements.forEach((canvas) => {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
      
      // Remove any remaining UI elements as fallback
      const uiElements = document.querySelectorAll('.top-bar, .side-panel, .toolbar');
      uiElements.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
      
      // Force garbage collection hint
      if (window.gc) {
        window.gc();
      }
    } catch (error) {
      console.warn('Error during container cleanup:', error);
      // Force clear the container even if cleanup fails
      this.appContainer.innerHTML = '';
      
      // Remove all canvas elements as nuclear option
      document.querySelectorAll('canvas').forEach(canvas => {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
    }
  }

  public getCurrentState(): AppState {
    return this.currentState;
  }

  public getSalmon(): Salmon | null {
    return this.salmon;
  }

  // Public methods to manually switch states
  public showLaunchingPage(): void {
    this.showLaunching();
  }

  public showLandingPage(): void {
    this.showLanding();
  }

  public showAuthPage(): void {
    this.showAuth();
  }

  // Navigation methods that also update URL
  public navigateToLaunching(): void {
    this.showLaunching();
  }

  public navigateToLanding(): void {
    this.showLanding();
  }

  public navigateToAuth(): void {
    this.showAuth();
  }

  public destroy(): void {
    try {
      if (this.unsubscribeAuth) {
        this.unsubscribeAuth();
        this.unsubscribeAuth = null;
      }
      
      if (this.uiManager) {
        this.uiManager.cleanup();
        this.uiManager = null;
      }
      
      if (this.salmon) {
        this.salmon.canvas.destroy();
        this.salmon = null;
      }
    } catch (error) {
      console.warn('Error during route manager cleanup:', error);
    }
  }
}
